import MatrixClientPeg from 'matrix-react-sdk/lib/MatrixClientPeg'
import dis from 'matrix-react-sdk/lib/dispatcher'
import Timer from 'matrix-react-sdk/lib/utils/Timer'
import { debounce } from 'util/functions'
import isEqual from 'fast-deep-equal'
import { MatrixEvent } from 'matrix-js-sdk'

export const UNAVAILABLE_TIME_MS = 5 * 60 * 1000
export const OFFLINE_TIME_MS = 15 * 60 * 1000
export const KEEPALIVE_TIME_MS = 20 * 1000
export const PRESENCE_STATES = [
  'online',
  'offline',
  'unavailable',
  'org.matrix.msc3026.busy',
  'unknown',
]
const ACCOUNT_PRESENCE_EVENT_TYPE = 'g.presence'
const MAX_RATE_LIMIT_RETRIES = 3
const DEFAULT_RETRY_DELAY_MS = 1000
const MIN_RETRY_DELAY_MS = 100
const MAX_RETRY_DELAY_MS = 30000
const PRESENCE_FAILURE_REPORT_COOLDOWN_MS = 60 * 1000
const PRESENCE_ACTIVITY_WINDOW_MS = 60 * 1000
const PRESENCE_CHANNEL = 'groove-presence-observability'
const PRESENCE_SOURCES = [
  'manual_ui',
  'account_data_echo',
  'auto_away',
  'auto_offline',
  'activity_recovery',
  'forced_keepalive',
]

export default class Presence {
  constructor() {
    this.autoPresenceTimer = null
    this.keepAliveTimer = null
    this.dispatcherRef = null
    this.client = null
    this.accountDataListener = null
    this.syncListener = null
    this.pendingEcho = null
    this.persistInFlight = 0
    this.syncBarrier = 0
    this.unavailableTimeMs = UNAVAILABLE_TIME_MS
    this.offlineTimeMs = OFFLINE_TIME_MS
    this.autoPresence = true
    this.state = 'unknown'
    this.confirmedState = 'offline'
    this.generation = 0
    this.processedGeneration = 0
    this.lifecycle = 0
    this.drainPromise = null
    this.activeEntry = null
    this.presenceQueue = []
    this.retryCancel = null
    this.persistence = Promise.resolve()
    this.lastPresenceFailureReportAt = -Infinity
    this.presenceActivity = {
      accepted: [],
      reused_active: [],
      reused_tail: [],
      idle_noop: [],
      put_attempt: [],
    }
    this.presenceChannel = null
    this.onAction = this.onAction.bind(this)
  }

  initClient(client, onAccountPresence) {
    if (!client) return false
    this.disposeClient()
    const lifecycle = this.lifecycle
    this.client = client
    this.onAccountPresence = onAccountPresence
    this.generation = 0
    this.processedGeneration = 0
    this.drainingLifecycle = null
    this.persistence = Promise.resolve()
    const ownUser = client.getUser && client.getUser(client.getUserId())
    this.confirmedState =
      ownUser && PRESENCE_STATES.includes(ownUser.presence)
        ? ownUser.presence
        : 'offline'
    this.state = this.confirmedState
    this.accountDataListener = event => {
      if (lifecycle === this.lifecycle && client === this.client) {
        this.onAccountData(event)
      }
    }
    client.on('accountData', this.accountDataListener)
    this.syncListener = state => {
      if (lifecycle !== this.lifecycle || client !== this.client) return
      if (state !== 'SYNCING') return
      if (this.syncBarrier > 0) this.syncBarrier -= 1
      this.reconcileEcho(client, lifecycle)
    }
    client.on('sync', this.syncListener)
    this.onAccountData(
      client.getAccountData(ACCOUNT_PRESENCE_EVENT_TYPE) ||
        this.getDefaultPresenceEvent(),
      true
    )
    this.initPresenceChannel()
    return true
  }

  onAccountData(event, initial = false) {
    if (!event || event.getType() !== ACCOUNT_PRESENCE_EVENT_TYPE) return
    const content = event.getContent() || {}
    const { unavailableTimeMs, offlineTimeMs, state, autoPresence } = content
    if (
      Number.isFinite(unavailableTimeMs) &&
      this.unavailableTimeMs !== unavailableTimeMs
    ) {
      this.setAutoAway(unavailableTimeMs, initial)
    }
    if (
      Number.isFinite(offlineTimeMs) &&
      this.offlineTimeMs !== offlineTimeMs
    ) {
      this.setAutoOffline(offlineTimeMs, initial)
    }
    if (!PRESENCE_STATES.includes(state) || state === 'unknown') {
      this.pendingEcho = null
    } else if (!this.canAdoptEcho()) {
      this.pendingEcho = {
        event,
        client: this.client,
        lifecycle: this.lifecycle,
      }
    } else {
      this.pendingEcho = null
    }
    if (
      this.canAdoptEcho() &&
      state !== this.state &&
      PRESENCE_STATES.includes(state)
    ) {
      if (initial) {
        this.setState(state, false, 'account_data_echo')
      } else if (state !== 'unknown') {
        this.state = state
        this.confirmedState = state
        this.generation += 1
        this.processedGeneration = this.generation
        MatrixClientPeg.updateSyncPresence(state)
        this.updateTimers()
      }
    }
    if (
      typeof autoPresence === 'boolean' &&
      this.autoPresence !== autoPresence
    ) {
      this.setAutoPresence(autoPresence, initial)
    }
    if (this.onAccountPresence) {
      this.onAccountPresence(
        this.canAdoptEcho()
          ? event
          : new MatrixEvent({
              type: ACCOUNT_PRESENCE_EVENT_TYPE,
              content: { ...content, state: this.state },
            })
      )
    }
  }

  canAdoptEcho() {
    return (
      this.generation === this.processedGeneration &&
      !this.activeEntry &&
      !this.presenceQueue.length &&
      this.persistInFlight === 0 &&
      this.syncBarrier === 0
    )
  }

  reconcileEcho(client = this.client, lifecycle = this.lifecycle) {
    if (!client || client !== this.client || lifecycle !== this.lifecycle)
      return
    if (!this.canAdoptEcho()) return
    const candidate = this.pendingEcho
    this.pendingEcho = null
    if (
      !candidate ||
      candidate.client !== client ||
      candidate.lifecycle !== lifecycle
    ) {
      return
    }
    this.onAccountData(candidate.event)
  }

  getDefaultPresenceEvent() {
    return new MatrixEvent({
      type: ACCOUNT_PRESENCE_EVENT_TYPE,
      content: this.constructSettingsContent(this.confirmedState),
    })
  }

  getPresenceEvent() {
    return this.client
      ? this.client.getAccountData(ACCOUNT_PRESENCE_EVENT_TYPE)
      : null
  }

  getSettings() {
    const event = this.getPresenceEvent()
    return event && event.getContent() ? event.getContent() : null
  }

  getAutoPresenceTimeoutMs(state) {
    if (state === 'online') return this.unavailableTimeMs
    if (['org.matrix.msc3026.busy', 'unavailable'].includes(state)) {
      return this.offlineTimeMs
    }
    return 24 * 60 * 60 * 1000
  }

  constructSettingsContent(state = this.confirmedState) {
    return {
      unavailableTimeMs: this.unavailableTimeMs,
      offlineTimeMs: this.offlineTimeMs,
      autoPresence: this.autoPresence,
      state,
    }
  }

  persistSettings(snapshot, client = this.client, lifecycle = this.lifecycle) {
    if (!client || client !== this.client || lifecycle !== this.lifecycle)
      return
    this.persistInFlight += 1
    this.persistence = this.persistence.then(async () => {
      let attempted = false
      try {
        if (client !== this.client || lifecycle !== this.lifecycle) return
        const event = client.getAccountData(ACCOUNT_PRESENCE_EVENT_TYPE)
        const oldSettings = event && event.getContent()
        if (isEqual(oldSettings, snapshot)) return
        attempted = true
        await client.setAccountData(ACCOUNT_PRESENCE_EVENT_TYPE, snapshot)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save presence settings: %s', err)
      } finally {
        if (client === this.client && lifecycle === this.lifecycle) {
          this.persistInFlight -= 1
          // Two syncs include a request sent after account-data HTTP settlement.
          if (attempted) this.syncBarrier = 2
          this.reconcileEcho(client, lifecycle)
        }
      }
    })
  }

  saveSettings = debounce(() => {
    this.persistSettings(this.constructSettingsContent())
  }, 100)

  async start() {
    this.startKeepAlive()
    if (!this.autoPresence) return
    this.autoPresenceTimer = new Timer(
      this.getAutoPresenceTimeoutMs(this.getState())
    )
    this.dispatcherRef = dis.register(this.onAction)
    while (this.autoPresenceTimer) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this.autoPresenceTimer.finished()
        if (this.getState() === 'online') {
          this.setState('unavailable', false, 'auto_away')
          this.autoPresenceTimer = new Timer(
            this.getAutoPresenceTimeoutMs('unavailable')
          )
          this.autoPresenceTimer.start()
        } else {
          this.setState('offline', false, 'auto_offline')
        }
      } catch (e) {
        /* timer aborted */
      }
    }
  }

  async startKeepAlive() {
    if (this.keepAliveTimer) return
    this.keepAliveTimer = new Timer(KEEPALIVE_TIME_MS)
    this.keepAliveTimer.start()
    while (this.keepAliveTimer) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this.keepAliveTimer.finished()
        if (['unavailable', 'org.matrix.msc3026.busy'].includes(this.state)) {
          this.setState(this.state, true, 'forced_keepalive')
        }
      } catch (e) {
        /* timer aborted */
      }
    }
  }

  stopTimers() {
    if (this.dispatcherRef) {
      dis.unregister(this.dispatcherRef)
      this.dispatcherRef = null
    }
    if (this.autoPresenceTimer) {
      this.autoPresenceTimer.abort()
      this.autoPresenceTimer = null
    }
    if (this.keepAliveTimer) {
      this.keepAliveTimer.abort()
      this.keepAliveTimer = null
    }
  }

  cancelRetry() {
    if (this.retryCancel) this.retryCancel()
  }

  disposeClient() {
    this.lifecycle += 1
    this.closePresenceChannel()
    this.cancelRetry()
    this.saveSettings.cancel()
    if (this.client && this.accountDataListener && this.client.removeListener) {
      this.client.removeListener('accountData', this.accountDataListener)
    }
    if (this.client && this.syncListener && this.client.removeListener) {
      this.client.removeListener('sync', this.syncListener)
    }
    this.syncListener = null
    this.pendingEcho = null
    this.persistInFlight = 0
    this.syncBarrier = 0
    this.client = null
    this.accountDataListener = null
    if (this.activeEntry) this.activeEntry.resolve()
    this.presenceQueue.forEach(entry => entry.resolve())
    this.presenceQueue = []
    this.drainPromise = null
    this.activeEntry = null
  }

  stop() {
    this.stopTimers()
    this.disposeClient()
  }

  updateTimers() {
    if (this.autoPresenceTimer) {
      this.autoPresenceTimer.restart()
      this.autoPresenceTimer.changeTimeout(
        this.getAutoPresenceTimeoutMs(this.state)
      )
    }
  }

  getState() {
    return this.state
  }

  now() {
    return performance.now()
  }

  getDurationBucket(duration) {
    if (duration < 1000) return '<1s'
    if (duration < 5000) return '1-5s'
    if (duration < 30000) return '5-30s'
    return '30s+'
  }

  getOfflineTimeoutBucket(timeout) {
    if (timeout < 60 * 1000) return '<1m'
    if (timeout < 5 * 60 * 1000) return '1-5m'
    if (timeout < 15 * 60 * 1000) return '5-15m'
    if (timeout === 15 * 60 * 1000) return '15m'
    return '15m+'
  }

  recordPresenceActivity(source, disposition) {
    if (
      !PRESENCE_SOURCES.includes(source) ||
      !Object.prototype.hasOwnProperty.call(this.presenceActivity, disposition)
    )
      return
    try {
      const now = this.now()
      const cutoff = now - PRESENCE_ACTIVITY_WINDOW_MS
      this.presenceActivity[disposition] = [
        ...this.presenceActivity[disposition],
        now,
      ]
        .filter(timestamp => timestamp >= cutoff)
        .slice(-21)
    } catch (err) {
      this.presenceActivity[disposition] = []
    }
  }

  getPresenceActivityCounts() {
    const counts = {}
    try {
      const cutoff = this.now() - PRESENCE_ACTIVITY_WINDOW_MS
      Object.keys(this.presenceActivity).forEach(disposition => {
        this.presenceActivity[disposition] = this.presenceActivity[disposition]
          .filter(timestamp => timestamp >= cutoff)
          .slice(-21)
        counts[disposition] = this.presenceActivity[disposition].length
      })
    } catch (err) {
      Object.keys(this.presenceActivity).forEach(disposition => {
        this.presenceActivity[disposition] = []
        counts[disposition] = 0
      })
    }
    return counts
  }

  getCountBucket(count) {
    if (!Number.isFinite(count) || count <= 0) return '0'
    if (count === 1) return '1'
    if (count <= 5) return '2-5'
    if (count <= 20) return '6-20'
    return '21+'
  }

  getQueueDepthBucket() {
    try {
      const depth = this.presenceQueue.length + (this.activeEntry ? 1 : 0)
      if (!depth) return '0'
      if (depth === 1) return '1'
      if (depth <= 5) return '2-5'
      return '6+'
    } catch (err) {
      return '0'
    }
  }

  getVisibilityBucket() {
    try {
      if (typeof document === 'undefined') return 'unknown'
      return ['visible', 'hidden', 'prerender'].includes(
        document.visibilityState
      )
        ? document.visibilityState
        : 'unknown'
    } catch (err) {
      return 'unknown'
    }
  }

  getTabCountBucket(count) {
    if (count === 1) return '1'
    if (count === 2) return '2'
    return '3+'
  }

  initPresenceChannel() {
    if (this.presenceChannel || typeof window === 'undefined') return
    try {
      const BroadcastChannel = window.BroadcastChannel
      if (!BroadcastChannel) return
      const channel = new BroadcastChannel(PRESENCE_CHANNEL)
      channel.onmessage = event => {
        try {
          const data = event.data
          if (data && data.type === 'presence_probe' && data.nonce) {
            channel.postMessage({ type: 'presence_here', nonce: data.nonce })
          }
        } catch (err) {
          // Telemetry must not affect presence coordination.
        }
      }
      this.presenceChannel = channel
    } catch (err) {
      this.presenceChannel = null
    }
  }

  closePresenceChannel() {
    if (!this.presenceChannel) return
    try {
      this.presenceChannel.close()
    } catch (err) {
      // Telemetry must not affect presence coordination.
    }
    this.presenceChannel = null
  }

  probePresenceTabs(callback) {
    let settled = false
    let timeout
    let channel
    let onMessage
    const finish = bucket => {
      if (settled) return
      settled = true
      try {
        clearTimeout(timeout)
        if (channel && onMessage)
          channel.removeEventListener('message', onMessage)
      } catch (err) {
        // Telemetry must not affect presence coordination.
      }
      try {
        callback(bucket)
      } catch (err) {
        // Telemetry must not affect presence coordination.
      }
    }
    try {
      channel = this.presenceChannel
      if (!channel || typeof channel.addEventListener !== 'function') {
        finish('unsupported')
        return
      }
      const nonce = `${this.now()}-${Math.random()}`
      const responders = new Set()
      onMessage = event => {
        try {
          const data = event.data
          if (data && data.type === 'presence_here' && data.nonce === nonce) {
            responders.add(data)
          }
        } catch (err) {
          finish('unsupported')
        }
      }
      channel.addEventListener('message', onMessage)
      timeout = setTimeout(
        () => finish(this.getTabCountBucket(responders.size + 1)),
        50
      )
      channel.postMessage({ type: 'presence_probe', nonce })
    } catch (err) {
      finish('unsupported')
    }
  }

  reportWriteFailure(entry, failure, retries, rateLimited) {
    const client = global.bugsnagClient
    if (!client) return
    let snapshot
    try {
      const now = this.now()
      if (
        now - this.lastPresenceFailureReportAt <
        PRESENCE_FAILURE_REPORT_COOLDOWN_MS
      )
        return
      this.lastPresenceFailureReportAt = now
      const counts = this.getPresenceActivityCounts()
      let failureCategory = 'other'
      if (rateLimited) failureCategory = 'rate_limited'
      else if (
        failure &&
        typeof failure === 'object' &&
        (failure.httpStatus === 403 || failure.errcode === 'M_FORBIDDEN')
      ) {
        failureCategory = 'forbidden'
      }
      snapshot = {
        requested_state: entry.state,
        forced_same_state: entry.forcedSameState,
        write_outcome: 'failed',
        failure_category: failureCategory,
        producer_source: entry.source,
        producer_disposition: entry.disposition,
        queue_depth_bucket: this.getQueueDepthBucket(),
        recent_accepted_bucket: this.getCountBucket(counts.accepted),
        recent_reused_active_bucket: this.getCountBucket(counts.reused_active),
        recent_reused_tail_bucket: this.getCountBucket(counts.reused_tail),
        recent_idle_noop_bucket: this.getCountBucket(counts.idle_noop),
        recent_put_attempt_bucket: this.getCountBucket(counts.put_attempt),
        visibility_bucket: this.getVisibilityBucket(),
        retry_count: retries,
        queue_wait_bucket: this.getDurationBucket(
          entry.writeStartedAt - entry.enqueuedAt
        ),
        write_elapsed_bucket: this.getDurationBucket(
          now - entry.writeStartedAt
        ),
      }
    } catch (err) {
      return
    }
    this.probePresenceTabs(tabCountBucket => {
      try {
        client.notify(new Error('Presence write failed'), event => {
          event.addMetadata('presenceWrite', {
            ...snapshot,
            tab_count_bucket: tabCountBucket,
          })
        })
      } catch (err) {
        // Telemetry must not affect presence coordination.
      }
    })
  }

  reportOfflineWrite(entry, outcome) {
    if (entry.state !== 'offline') return
    try {
      const client = global.bugsnagClient
      if (!client) return
      const snapshot = {
        producer_source: entry.source,
        previous_state: entry.previousState,
        write_outcome: outcome,
        offline_timeout_bucket: this.getOfflineTimeoutBucket(
          this.offlineTimeMs
        ),
        visibility_bucket: this.getVisibilityBucket(),
      }
      this.probePresenceTabs(tabCountBucket => {
        try {
          client.notify(new Error('Presence offline write'), event => {
            event.addMetadata('presenceWrite', {
              ...snapshot,
              tab_count_bucket: tabCountBucket,
            })
          })
        } catch (err) {
          // Telemetry must not affect presence coordination.
        }
      })
    } catch (err) {
      // Telemetry must not affect presence coordination.
    }
  }

  onAction(payload) {
    if (
      this.state !== 'unknown' &&
      this.client &&
      payload.action === 'user_activity' &&
      this.autoPresence
    ) {
      if (this.state !== 'online') {
        this.setState('online', false, 'activity_recovery')
      } else if (this.autoPresenceTimer) {
        this.autoPresenceTimer.restart()
        this.autoPresenceTimer.changeTimeout(
          this.getAutoPresenceTimeoutMs('online')
        )
      }
    }
  }

  async setAutoAway(unavailableTimeMs, persist = true) {
    this.unavailableTimeMs = unavailableTimeMs
    this.updateTimers()
    if (persist) this.saveSettings()
  }

  async setAutoOffline(offlineTimeMs, persist = true) {
    this.offlineTimeMs = offlineTimeMs
    this.updateTimers()
    if (persist) this.saveSettings()
  }

  async setAutoPresence(autoPresence, persist = true) {
    if (autoPresence === null || this.autoPresence === autoPresence) return
    this.autoPresence = autoPresence
    if (!this.autoPresenceTimer && autoPresence === true) this.start()
    else if (this.autoPresenceTimer && autoPresence === false) this.stopTimers()
    if (persist) this.saveSettings()
  }

  setState(newState, force = false, source = 'manual_ui') {
    if (!PRESENCE_STATES.includes(newState)) {
      return Promise.reject(new Error(`Bad presence state: ${newState}`))
    }
    const producer = PRESENCE_SOURCES.includes(source) ? source : 'manual_ui'
    if (force && this.activeEntry && newState === this.activeEntry.state) {
      this.recordPresenceActivity(producer, 'reused_active')
      return this.activeEntry.promise
    }
    if (!force) {
      const queueTail = this.presenceQueue[this.presenceQueue.length - 1]
      if (queueTail && queueTail.state === newState) {
        this.recordPresenceActivity(producer, 'reused_tail')
        return queueTail.promise
      }
      if (
        !queueTail &&
        this.activeEntry &&
        this.activeEntry.state === newState
      ) {
        this.recordPresenceActivity(producer, 'reused_active')
        return this.activeEntry.promise
      }
      if (!this.activeEntry && !queueTail && newState === this.state) {
        this.recordPresenceActivity(producer, 'idle_noop')
        return Promise.resolve()
      }
    }
    const forcedSameState = force && newState === this.state
    const previousState = PRESENCE_STATES.includes(this.state)
      ? this.state
      : 'unknown'
    if (this.keepAliveTimer) this.keepAliveTimer.restart()
    this.state = newState
    this.generation += 1
    MatrixClientPeg.updateSyncPresence(newState)
    let resolve
    const promise = new Promise(res => {
      resolve = res
    })
    this.recordPresenceActivity(producer, 'accepted')
    this.presenceQueue.push({
      state: newState,
      previousState,
      generation: this.generation,
      forcedSameState,
      resolve,
      promise,
      enqueuedAt: this.now(),
      source: producer,
      disposition: 'accepted',
    })
    this.startDrain()
    return promise
  }

  startDrain() {
    if (this.drainPromise) return this.drainPromise
    const lifecycle = this.lifecycle
    const client = this.client
    const drainPromise = this.drainPresence(lifecycle, client)
    this.drainPromise = drainPromise
    const cleanup = error => {
      if (
        this.lifecycle !== lifecycle ||
        this.client !== client ||
        this.drainPromise !== drainPromise
      ) {
        return
      }
      if (error) {
        // eslint-disable-next-line no-console
        console.error('Presence coordinator failed: %s', error)
        if (this.activeEntry) this.activeEntry.resolve()
        this.activeEntry = null
        this.presenceQueue.forEach(entry => entry.resolve())
        this.presenceQueue = []
        this.processedGeneration = this.generation
        this.state = this.confirmedState
        MatrixClientPeg.updateSyncPresence(this.confirmedState)
        this.updateTimers()
      }
      this.drainPromise = null
      if (this.presenceQueue.length) this.startDrain()
      else this.reconcileEcho(client, lifecycle)
    }
    drainPromise.then(() => cleanup(), cleanup)
    return drainPromise
  }

  sleep(ms) {
    return new Promise(resolve => {
      let timeout
      const finish = () => {
        clearTimeout(timeout)
        if (this.retryCancel === finish) this.retryCancel = null
        resolve()
      }
      timeout = setTimeout(finish, ms)
      this.retryCancel = finish
    })
  }

  isRateLimitError(err) {
    if (!err) return false
    if (typeof err.isRateLimitError === 'function' && err.isRateLimitError()) {
      return true
    }
    return err.httpStatus === 429 || err.errcode === 'M_LIMIT_EXCEEDED'
  }

  getRetryDelay(err) {
    const sdkDelay =
      typeof err.getRetryAfterMs === 'function'
        ? err.getRetryAfterMs()
        : err.data && err.data.retry_after_ms
    if (!Number.isFinite(sdkDelay)) return DEFAULT_RETRY_DELAY_MS
    return Math.min(MAX_RETRY_DELAY_MS, Math.max(MIN_RETRY_DELAY_MS, sdkDelay))
  }

  async drainPresence(lifecycle, client) {
    while (
      lifecycle === this.lifecycle &&
      client === this.client &&
      this.presenceQueue.length
    ) {
      const entry = this.presenceQueue.shift()
      this.activeEntry = entry
      entry.writeStartedAt = this.now()
      const suppressPersistence =
        entry.forcedSameState && entry.state === this.confirmedState
      let retries = 0
      let failure = null
      let failureIsRateLimited = false
      while (lifecycle === this.lifecycle && client === this.client) {
        this.recordPresenceActivity(entry.source, 'put_attempt')
        try {
          // eslint-disable-next-line no-await-in-loop
          await client.setPresence(
            entry.state === 'unavailable'
              ? 'org.matrix.msc3026.busy'
              : entry.state
          )
          failure = null
          break
        } catch (err) {
          failure = err
          failureIsRateLimited = this.isRateLimitError(err)
          if (
            !failureIsRateLimited ||
            entry.source === 'forced_keepalive' ||
            retries >= MAX_RATE_LIMIT_RETRIES
          ) {
            break
          }
          if (lifecycle !== this.lifecycle || client !== this.client) return
          retries += 1
          // eslint-disable-next-line no-await-in-loop
          await this.sleep(this.getRetryDelay(err))
        }
      }
      if (lifecycle !== this.lifecycle || client !== this.client) return
      this.processedGeneration = entry.generation
      if (!failure) {
        if (!suppressPersistence) {
          this.persistSettings(
            this.constructSettingsContent(entry.state),
            client,
            lifecycle
          )
        }
        this.confirmedState = entry.state
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to set presence: %s', failure)
        this.reportWriteFailure(entry, failure, retries, failureIsRateLimited)
        if (entry.generation === this.generation) {
          this.state = this.confirmedState
          MatrixClientPeg.updateSyncPresence(this.confirmedState)
          this.updateTimers()
          if (
            !suppressPersistence &&
            !failureIsRateLimited &&
            !this.pendingEcho
          ) {
            this.persistSettings(
              this.constructSettingsContent(this.confirmedState),
              client,
              lifecycle
            )
          }
        }
      }
      this.reportOfflineWrite(entry, failure ? 'failed' : 'succeeded')
      this.activeEntry = null
      entry.resolve()
    }
  }
}
